<script lang="ts" setup>
  import { useCssModule } from 'vue';

  import { t } from '../../locale';
  import Button from '../Button/Button.vue';
  import Dropdown from '../Dropdown/Dropdown.vue';
  import Icon from '../Icon/Icon.vue';

  defineOptions({
    name: 'll-actions-dropdown',
  });

  export interface ActionsDropdownProps {
    /**
     * Used to position the dropdown relative to target
     * Options: left, right
     */
    align?: 'left' | 'right';
    /**
     * Indicates whether the trigger button is disabled or not
     */
    disabled?: boolean;
    /**
     * Class to be applied to the dropdown content
     */
    contentClass?: string;
    /**
     * Text to be displayed on the trigger button
     */
    buttonText?: string;
  }

  const props = withDefaults(defineProps<ActionsDropdownProps>(), {
    align: 'left',
    disabled: false,
    contentClass: '',
    buttonText: () => t('ll.actions'),
  });

  const classes = useCssModule();
</script>

<template>
  <Dropdown
    :align="props.align"
    :content-class="[classes.dropdownContent, props.contentClass]"
    :class="classes.actionsDropdown"
  >
    <template #toggle="{ isActive, toggle }">
      <Button
        class="button flex w-full items-center justify-between border border-blue-500 text-blue-500"
        secondary
        :aria-expanded="isActive.toString()"
        :class="[classes.actionButton, { [classes.disabled]: props.disabled }]"
        :disabled="props.disabled"
        @click="toggle"
      >
        {{ props.buttonText }} <Icon class="ml-1.5" name="caret-down" />
      </Button>
    </template>

    <template v-if="!props.disabled" #default>
      <!-- @slot Dropdown content -->
      <slot></slot>
    </template>
  </Dropdown>
</template>

<style module>
  @reference "../../../styles/main.css";

  @layer utilities {
    .actionButton {
      min-width: 156px;
      white-space: normal;
    }

    .actionButton:hover {
      background-color: var(--color-blue-100);
      border-color: var(--color-blue-500) !important;
    }

    .disabled {
      background-color: var(--color-ice-100) !important;
      border-color: var(--color-ice-500) !important;
      color: var(--color-ice-500) !important;
    }

    .dropdownContent {
      margin-top: 4px;
    }

    .dropdownContent:global(.dropdown__content) {
      margin-left: 0;
      margin-right: 0;
      max-width: 90vw;
    }

    .dropdownContent:global(.dropdown__content)::after {
      display: none;
    }

    @media (width >= theme(--breakpoint-md)) {
      .actionsDropdown {
        min-width: 156px;
      }

      .dropdownContent:global(.dropdown__content) {
        min-width: 156px;
      }
    }
  }
</style>
